import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { CALIBRATION_DAYS } from "@opus-dt/shared-types";
import { SlideOver } from "@/components/ui/slide-over";
import { DateTimeField } from "@/components/ui/datetime-field";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { SandboxPeriod } from "@/types/sim-sandbox.types";
import { OpPeriodPicker } from "./OpPeriodPicker";
import { operatingHoursLabel } from "./calendar/hours-label";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  period: SandboxPeriod;
  onChange: (patch: Partial<SandboxPeriod>) => void;
  /** Linha base do cenário; null ⇒ linha nova (sem opção de Ordem de Produção). */
  lineId: string | null;
  /** Prefill do produto no modo OP (produto vem da OP). */
  onProduct?: (productId: string) => void;
  /** Horas corridas × operacionais (∩ calendário) do período selecionado. */
  runningHours: number | null;
  effectiveHours: number | null;
}

// Período em drawer lateral (re-layout do sandbox, fiel ao V3PeriodModal):
// intervalo livre (par de datetime-field, fim > início) ou Ordem de Produção (janela/produto da OP).
// "Avançado" = janela de calibração (discreta 15/30/60/90, default 90). Período (HORIZONTE simulado)
// ≠ janela de calibração (histórico p/ MTBF/MTTR).
export function PeriodDrawer({
  open,
  onOpenChange,
  period,
  onChange,
  lineId,
  onProduct,
  runningHours,
  effectiveHours,
}: Props) {
  const [advanced, setAdvanced] = useState(false);
  const isOp = period.mode !== "FREE";
  const invalidEnd =
    period.periodStart != null &&
    period.periodEnd != null &&
    new Date(period.periodEnd) <= new Date(period.periodStart);

  return (
    <SlideOver open={open} onOpenChange={onOpenChange} title="Período da simulação" width={460}>
      <div className="space-y-3 p-4">
        <p className="text-[11px] text-muted-foreground">
          O período é o horizonte simulado. A janela de calibração (avançado) é o histórico usado
          para derivar MTBF/MTTR — não confundir.
        </p>

        {effectiveHours != null ? (
          <p className="rounded-md bg-muted px-2.5 py-1.5 text-[11px] text-text-secondary">
            {operatingHoursLabel(runningHours, effectiveHours)} — só as horas dentro do expediente
            do calendário contam na simulação.
          </p>
        ) : null}

        {lineId ? (
          <div className="inline-flex rounded-md bg-muted p-0.5 text-xs">
            <button
              type="button"
              className={cn("rounded px-2 py-1", !isOp && "bg-background font-medium shadow-sm")}
              onClick={() => onChange({ mode: "FREE", sourceProductionOrderId: null })}
            >
              Intervalo livre
            </button>
            <button
              type="button"
              className={cn("rounded px-2 py-1", isOp && "bg-background font-medium shadow-sm")}
              onClick={() => onChange({ mode: "OP_PLANNED" })}
            >
              Ordem de Produção
            </button>
          </div>
        ) : null}

        {isOp && lineId ? (
          <OpPeriodPicker
            lineId={lineId}
            period={period}
            onChange={onChange}
            onProduct={onProduct}
          />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <DateTimeField
                id="sbx-start"
                label="Início"
                value={period.periodStart}
                onChange={(v) => onChange({ periodStart: v })}
              />
              <DateTimeField
                id="sbx-end"
                label="Fim"
                value={period.periodEnd}
                minIso={period.periodStart ?? undefined}
                invalid={invalidEnd}
                onChange={(v) => onChange({ periodEnd: v })}
              />
            </div>
            {invalidEnd ? (
              <p className="text-xs text-destructive" role="alert">
                O fim deve ser posterior ao início.
              </p>
            ) : null}
          </>
        )}

        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setAdvanced((v) => !v)}
        >
          {advanced ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          Avançado · janela de calibração
        </button>
        {advanced ? (
          <div className="space-y-1">
            <Label className="text-xs">Janela de calibração (dias)</Label>
            <div className="flex gap-1">
              {CALIBRATION_DAYS.map((d) => {
                const active = (period.calibrationDays ?? 90) === d;
                return (
                  <Button
                    key={d}
                    type="button"
                    size="sm"
                    variant={active ? "default" : "outline"}
                    onClick={() => onChange({ calibrationDays: d })}
                  >
                    {d}
                  </Button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Só MTBF/MTTR vêm da janela; ritmo/ciclo/qualidade seguem a premissa. Janelas curtas
              reduzem o nível de confiança.
            </p>
          </div>
        ) : null}

        <div className="flex justify-end border-t pt-3">
          <Button size="sm" disabled={invalidEnd} onClick={() => onOpenChange(false)}>
            Usar período
          </Button>
        </div>
      </div>
    </SlideOver>
  );
}
